import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Trash2, Undo2 } from "lucide-react";

interface CommunityRecord {
  name: string;
  description?: string | null;
  members_count?: number | null;
  growth_percentage?: number | null;
  plant_hardiness_zone?: string | null;
}

export const CommunitiesImporter = () => {
  const [isImporting, setIsImporting] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [csvData, setCsvData] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setCsvData(content);
      };
      reader.readAsText(selectedFile);
    }
  };

  const parseCsvData = (csvText: string): CommunityRecord[] => {
    const lines = csvText.trim().split("\n");
    if (lines.length < 2) return [];

    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());

    const data: CommunityRecord[] = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      const values = lines[i].split(",").map((v) => v.trim());
      const rec: CommunityRecord = { name: "" };

      headers.forEach((header, idx) => {
        const val = values[idx];
        if (val === undefined || val === null || val === '') return;
        
        // Clean the value to remove any unwanted characters
        const cleanedVal = val.toString().trim();
        
        if (header.includes("name") || header.includes("community")) {
          rec.name = cleanedVal;
        } else if (header.includes("description") || header === "desc") {
          rec.description = cleanedVal || null;
        } else if (header.includes("members")) {
          const n = parseInt(cleanedVal.replace(/[^0-9.-]/g, ""), 10);
          rec.members_count = isNaN(n) ? 0 : n;
        } else if (header.includes("growth")) {
          const n = parseFloat(cleanedVal.replace(/[^0-9.-]/g, ""));
          rec.growth_percentage = isNaN(n) ? 0 : n;
        } else if (header.includes("zone")) {
          rec.plant_hardiness_zone = cleanedVal || null;
        }
      });

      if (rec.name && rec.name.trim().length > 0) {
        data.push(rec);
      }
    }

    return data;
  };

  const handleImport = async () => {
    if (!csvData.trim()) {
      toast({
        title: "Error",
        description: "Please paste CSV data or select a file first.",
        variant: "destructive",
      });
      return;
    }

    setIsImporting(true);
    try {
      const parsed = parseCsvData(csvData);
      if (parsed.length === 0) {
        toast({
          title: "Error",
          description: "No valid data found in the CSV. Check headers and rows.",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke("import-communities", {
        body: { data: parsed },
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: data?.message || `Imported ${parsed.length} communities successfully.`,
      });

      setCsvData("");
      setFile(null);
    } catch (err: any) {
      console.error("Communities import error:", err);
      toast({
        title: "Error",
        description: `Failed to import communities: ${err?.message || err}`,
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleUndoLastImport = async () => {
    setIsUndoing(true);
    try {
      const { data, error } = await supabase.functions.invoke("import-communities", {
        body: { action: "undo_last" },
        method: "DELETE"
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: data?.message || "Last communities import has been undone.",
      });
    } catch (error: any) {
      console.error("Undo communities error:", error);
      toast({
        title: "Error",
        description: `Failed to undo last import: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsUndoing(false);
    }
  };

  const handleClearAllCommunities = async () => {
    if (!confirm("Are you sure you want to delete ALL communities? This action cannot be undone.")) {
      return;
    }

    setIsClearing(true);
    try {
      const { data, error } = await supabase.functions.invoke("import-communities", {
        body: { action: "clear_all" },
        method: "DELETE"
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: data?.message || "All communities have been cleared successfully.",
      });
    } catch (error: any) {
      console.error('Clear communities error:', error);
      toast({
        title: "Error",
        description: `Failed to clear communities: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Communities Importer
        </CardTitle>
        <CardDescription>
          Import community records from CSV. Expected columns: name, description, members_count, growth_percentage, plant_hardiness_zone
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Upload CSV File</label>
          <Input type="file" accept=".csv,.txt" onChange={handleFileChange} className="mb-2" />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Or Paste CSV Data</label>
          <Textarea
            placeholder="Paste your CSV data here..."
            value={csvData}
            onChange={(e) => setCsvData(e.target.value)}
            rows={10}
            className="font-mono text-sm"
          />
        </div>

        <div className="flex gap-2">
          <Button onClick={handleImport} disabled={isImporting || !csvData.trim()} className="flex-1">
            {isImporting ? "Importing..." : "Import Communities"}
          </Button>

          <Button 
            onClick={handleUndoLastImport}
            disabled={isUndoing}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <Undo2 className="h-4 w-4" />
            {isUndoing ? "Undoing..." : "Undo Last"}
          </Button>

          <Button 
            onClick={handleClearAllCommunities}
            disabled={isClearing}
            variant="destructive"
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {isClearing ? "Clearing..." : "Clear All"}
          </Button>
        </div>

        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
          <strong>CSV Format Example:</strong>
          <br />
          name,description,members_count,growth_percentage,plant_hardiness_zone
          <br />
          Midwest Corn Growers,Regional corn community,1250,12.5,5b
          <br />
          Southern Tomato Club,All about tomatoes,600,8.2,7a
        </div>
      </CardContent>
    </Card>
  );
}
